import { candidateStore, supporterStore } from "./mockApi";
import type {
  CandidateProfile,
  CandidateSubmission,
  CandidateStatus,
  DonationIntentResult,
  DonationReadiness,
  DonationSubmission,
  Supporter
} from "./types";

const defaultApiBase = "https://bliss-it-services.pages.dev";
const apiBase = (import.meta.env.VITE_BLISS_API_BASE_URL || defaultApiBase).replace(/\/$/, "");

type ApiResponse<T> = { success: true; data: T } | { success: false; error: { message: string } };

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${apiBase}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {})
    }
  });
  const payload = (await response.json()) as ApiResponse<T>;
  if (!response.ok || !payload.success) {
    throw new Error(payload.success ? "Request failed" : payload.error.message);
  }
  return payload.data;
}

export const candidateApi = {
  async listPublic(): Promise<CandidateProfile[]> {
    try {
      const data = await request<{ candidates: CandidateProfile[] }>("/api/candidate-platform/candidates");
      return data.candidates;
    } catch {
      return candidateStore.list().filter((candidate) => candidate.status === "approved");
    }
  },
  async listAll(): Promise<CandidateProfile[]> {
    try {
      const data = await request<{ candidates: CandidateProfile[] }>("/api/candidate-platform/candidates?includeAll=1");
      return data.candidates;
    } catch {
      return candidateStore.list();
    }
  },
  async submit(submission: CandidateSubmission): Promise<CandidateProfile> {
    try {
      const data = await request<{ candidate: CandidateProfile }>("/api/candidate-platform/candidates", {
        method: "POST",
        body: JSON.stringify(submission)
      });
      return data.candidate;
    } catch {
      return candidateStore.submit(submission);
    }
  },
  async update(candidate: CandidateProfile): Promise<CandidateProfile> {
    try {
      const data = await request<{ candidate: CandidateProfile }>(`/api/candidate-platform/candidates/${candidate.id}`, {
        method: "PUT",
        body: JSON.stringify(candidate)
      });
      return data.candidate;
    } catch {
      return candidateStore.update(candidate);
    }
  },
  async review(id: string, status: CandidateStatus, note: string): Promise<CandidateProfile | null> {
    try {
      const data = await request<{ candidate: CandidateProfile }>(`/api/candidate-platform/candidates/${id}/review`, {
        method: "POST",
        body: JSON.stringify({ status, note })
      });
      return data.candidate;
    } catch {
      candidateStore.setStatus(id, status, note);
      return null;
    }
  },
  async follow(candidateId: string, supporter: Omit<Supporter, "id" | "candidateId" | "createdAt">): Promise<void> {
    try {
      await request<{ supporter: Supporter }>(`/api/candidate-platform/candidates/${candidateId}/supporters`, {
        method: "POST",
        body: JSON.stringify(supporter)
      });
    } catch {
      supporterStore.follow(candidateId, supporter);
    }
  },
  async donationReadiness(candidateId: string): Promise<DonationReadiness> {
    const data = await request<{ readiness: DonationReadiness }>(
      `/api/candidate-platform/candidates/${candidateId}/donation-readiness`
    );
    return data.readiness;
  },
  async prepareDonation(candidateId: string, donation: DonationSubmission): Promise<DonationIntentResult> {
    return request<DonationIntentResult>(`/api/candidate-platform/candidates/${candidateId}/donation-intents`, {
      method: "POST",
      body: JSON.stringify(donation)
    });
  }
};
